import React from 'react';
import { Tab } from '@sharefiledev/flow-web';
import { Skeleton, Tag } from 'antd';
import { AppContextProvider } from '../AppContext';
import { folderTabKey } from '../folderTabEntry';
import { LoadingSpinner } from '../LoadingSpinner';
import { LocalizationLoader, t } from '../translate';
import type {
	AttachmentValidationLabelProps,
	ClientDashboardAttachmentsDrawerProps,
	ClientDashboardAttachmentsTabLabelProps,
	DocumentRequestMenuProps,
	ProjectDeletedResourceTabProps,
	ProjectRequestListTabProps,
	ProjectRequestListTileProps,
	ShareButtonProps,
	SpecEditorProps,
	TaskBadgeProps,
	TaskProgressBarProps,
} from './extensionSlotTypes';

const LazyRequestListTabExtensionSlot = React.lazy(
	() => import('./ProjectRequestListTab')
);
export const AsyncRequestListTabExtensionSlot: React.FC<
	ProjectRequestListTabProps
> = props => {
	/**
	 * Returning a "Tab" instance will degrade the entry bundle into pulling in
	 * Flow-Web, but the "Tabs" component does not properly resize on new Tab content.
	 */
	return (
		<Tab
			tabContext={props.tabContext}
			value={folderTabKey}
			label={<LocalizationLoader>{() => t('listsTab.tabLabel')}</LocalizationLoader>}
			data-analytics-id="documents-requests-tab"
		>
			<React.Suspense fallback={<LoadingSpinner />}>
				<LazyRequestListTabExtensionSlot {...props} />
			</React.Suspense>
		</Tab>
	);
};

const LazyProjectRequestListTile = React.lazy(() => import('./ProjectRequestListTile'));
export const AsyncProjectRequestListTileExtensionSlot: React.FC<
	ProjectRequestListTileProps
> = props => {
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectRequestListTile {...props} />
		</React.Suspense>
	);
};

const LazyProjectDeletedResourceTab = React.lazy(
	() => import('./ProjectDeletedResourceTab')
);
export const AsyncProjectDeletedResourceTabExtensionSlot: React.FC<
	ProjectDeletedResourceTabProps
> = props => {
	/**
	 * Returning a "Tab" instance will degrade the entry bundle into pulling in
	 * Flow-Web, but the "Tabs" component does not properly resize on new Tab content.
	 */
	return (
		<Tab
			tabContext={props.tabContext}
			value={folderTabKey}
			label={<LocalizationLoader>{() => t('listsTab.tabLabel')}</LocalizationLoader>}
			data-analytics-id="documents-requests-tab"
		>
			<React.Suspense fallback={<LoadingSpinner />}>
				<LazyProjectDeletedResourceTab {...props} />
			</React.Suspense>
		</Tab>
	);
};

const LazyTaskBadgeExtensionSlot = React.lazy(() => import('./TaskBadge'));
export const AsyncTaskBadgeExtensionSlot: React.FC<TaskBadgeProps> = props => {
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyTaskBadgeExtensionSlot {...props} />
		</React.Suspense>
	);
};

const LazyClientDashboardAttachmentsDrawerExtensionSlot = React.lazy(
	() => import('./ClientDashboardAttachmentsDrawer')
);
export const AsyncClientDashboardAttachmentsDrawerExtensionSlot: React.FC<
	ClientDashboardAttachmentsDrawerProps
> = props => {
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyClientDashboardAttachmentsDrawerExtensionSlot {...props} />
		</React.Suspense>
	);
};

const LazyClientDashboardAttachmentsTabLabelExtensionSlot = React.lazy(
	() => import('./AttachmentsTabLabel')
);
export const AsyncClientDashboardAttachmentsTabLabelExtensionSlot: React.FC<
	ClientDashboardAttachmentsTabLabelProps
> = props => {
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<AppContextProvider piletApi={props.piletApi}>
				<LazyClientDashboardAttachmentsTabLabelExtensionSlot {...props} />
			</AppContextProvider>
		</React.Suspense>
	);
};

const LazyShareButtonExtensionSlot = React.lazy(() => import('./ShareButton'));
export const AsyncShareButtonExtensionSlot: React.FC<ShareButtonProps> = props => {
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<AppContextProvider piletApi={props.piletApi}>
				<LazyShareButtonExtensionSlot {...props} />
			</AppContextProvider>
		</React.Suspense>
	);
};

const LazySpecEditorExtensionSlot = React.lazy(() => import('./SpecEditor'));
export const AsyncSpecEditorExtensionSlot: React.FC<SpecEditorProps> = props => {
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<AppContextProvider piletApi={props.piletApi}>
				<LazySpecEditorExtensionSlot {...props} />
			</AppContextProvider>
		</React.Suspense>
	);
};

const LazyTaskProgressBarExtension = React.lazy(() => import('./TaskProgressBar'));
export const AsyncTaskProgressBarExtension: React.FC<TaskProgressBarProps> = props => {
	return (
		<React.Suspense fallback={<Skeleton.Button shape="round" active />}>
			<AppContextProvider piletApi={props.piletApi}>
				<LazyTaskProgressBarExtension {...props} />
			</AppContextProvider>
		</React.Suspense>
	);
};

const LazyAttachmentValidationLabel = React.lazy(
	() => import('./AttachmentValidationLabel')
);
export const AsyncAttachmentValidationLabel: React.FC<
	AttachmentValidationLabelProps
> = props => {
	return (
		<React.Suspense fallback={<Tag />}>
			<AppContextProvider piletApi={props.piletApi}>
				<LazyAttachmentValidationLabel {...props} />
			</AppContextProvider>
		</React.Suspense>
	);
};

const LazyDocumentRequestActions = React.lazy(
	() => import('./DocumentRequestActionsMenu')
);
export const AsyncDocumentRequestActionsMenu: React.FC<
	DocumentRequestMenuProps
> = props => {
	return (
		<React.Suspense fallback={null}>
			<AppContextProvider piletApi={props.piletApi}>
				<LazyDocumentRequestActions {...props} />
			</AppContextProvider>
		</React.Suspense>
	);
};
